import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { apiClient } from '@/lib/apiClient';
import { useCart } from '@/features/cart/context/CartContext';
import WholesaleNotice from './WholesaleNotice';
import '../products.css';
import { useNavigate } from 'react-router-dom';
import { resolveUnitPrice } from '@/lib/pricing';
import { fmt } from '@/lib/format';


const imgUrl = (url) => (url?.startsWith('/') ? `${url}` : url);

export default function ProductDetailView() {
  const navigate = useNavigate();
  const { id } = useParams();
  const {
    addToCart,
    cart,
    updateQuantity,
    removeFromCart,
    toast,
    canShop,
  } = useCart();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedQuantity, setSelectedQuantity] = useState(1);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const { data, error } = await apiClient.GET('/api/v1/products/{product_id}', {
          params: { path: { product_id: id } },
        });
        if (!error && data) setProduct(data);
      } catch (err) {
        console.error('Error fetching product:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id]);

  if (loading) {
    return (
      <div className="container product-detail-page" style={{ textAlign: 'center' }}>
        در حال بارگذاری...
      </div>
    );
  }

  if (!product) {
    return (
      <div className="container product-detail-page" style={{ textAlign: 'center' }}>
        محصول مورد نظر یافت نشد.
      </div>
    );
  }

  const cartItem = cart.find((item) => item.id === product.id);
  const categories = product.categories ?? [];
  const isOutOfStock = product.stock_quantity <= 0;
  // Before the item is in the cart, the wholesale notice reacts to the
  // local stepper below; once added, it reflects the actual cart quantity
  // (same source the price line above already uses).
  const previewQuantity = cartItem ? cartItem.quantity : selectedQuantity;

  return (
    <div className="container product-detail-page">
      {toast && <div className="product-toast">✓ {toast}</div>}

      <button onClick={() => navigate(-1)} className="product-detail-back">
      ← بازگشت به محصولات
      </button>

      <div className="product-detail-card">
        <div className="product-detail-image">
          {product.image_url ? (
            <img src={imgUrl(product.image_url)} alt={product.name_fa} />
          ) : (
            <span className="product-detail-image-placeholder">بدون تصویر</span>
          )}
        </div>

        <div className="product-detail-info">
          <h1>{product.name_fa}</h1>

          {categories.length > 0 && (
            <div className="product-detail-categories">
              {categories.map((category) => (
                <span key={category.id} className="product-detail-category">
                  {category.name_fa}
                </span>
              ))}
            </div>
          )}

          {product.brand && <p className="product-detail-brand">برند: {product.brand}</p>}
          {product.description && <p className="product-detail-description">{product.description}</p>}

          <p className={`product-detail-stock ${isOutOfStock ? 'out-of-stock' : 'in-stock'}`}>
            موجودی: {fmt(product.stock_quantity)}
          </p>

          <p className="product-detail-price">
            {fmt(resolveUnitPrice(product, previewQuantity))} تومان
          </p>
          
          <WholesaleNotice item={product} quantity={previewQuantity} />

          {canShop ? (
            cartItem ? (
              <div className="product-detail-qty">
                <button
                  className="product-detail-qty-btn"
                  onClick={() =>
                    cartItem.quantity <= 1
                      ? removeFromCart(product.id)
                      : updateQuantity(product.id, cartItem.quantity - 1)
                  }
                >
                  −
                </button>
                <span className="product-detail-qty-value">{fmt(cartItem.quantity)}</span>
                <button
                  className="product-detail-qty-btn"
                  disabled={cartItem.quantity >= product.stock_quantity}
                  onClick={() => updateQuantity(product.id, cartItem.quantity + 1)}
                >
                  +
                </button>
              </div>
            ) : (
              <>
               {!isOutOfStock && (
                 <div className="product-detail-qty">
                   <button
                     className="product-detail-qty-btn"
                     disabled={selectedQuantity <= 1}
                     onClick={() => setSelectedQuantity((q) => Math.max(1, q - 1))}
                   >
                     −
                   </button>
                   <span className="product-detail-qty-value">{fmt(selectedQuantity)}</span>
                   <button
                     className="product-detail-qty-btn"
                     disabled={selectedQuantity >= product.stock_quantity}
                     onClick={() =>
                       setSelectedQuantity((q) => Math.min(q + 1, product.stock_quantity))
                     }
                   >
                     +
                   </button>
                 </div>
               )}
               <button
                 className="product-detail-add"
                 disabled={isOutOfStock}
                 onClick={() => addToCart(product, selectedQuantity)}
               >
                 {isOutOfStock ? 'ناموجود' : 'افزودن به سبد خرید'}
               </button>
             </>
            )
          ) : (
            <div className="product-detail-role-gate">
              <i className="fas fa-info-circle" />
              برای خرید باید به عنوان <strong>مشتری</strong> ثبت‌نام کنید.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}